import {ANON_ROLE_UID} from "@buildwithflux/constants";
import {
    isEnterpriseUid,
    isOrganizationUid,
    OrganizationUid,
    PrefixedUserUid,
    UserUid,
} from "@buildwithflux/identifiers-schema/legacy";
import {userUidForms} from "@buildwithflux/identifiers-schema/principals/UserUid";
import {z} from "zod";

import {DocIdSuperseded} from "./docIdSuperseded";
import {
    DocumentPermission,
    documentPermissionToPermissionType,
    PermissionType,
    permissionTypeToDocumentPermission,
} from "./permissionType";

/**
 * Key of the document permissions map - principals that can have permissions on a document.
 * Includes user UIDs, organization UIDs, and the anonymous role UID.
 */
const DocumentPermissionsKey = z.union([UserUid, OrganizationUid, z.literal(ANON_ROLE_UID)]);
type DocumentPermissionsKey = z.infer<typeof DocumentPermissionsKey>;

/**
 * This is the type of "roles" subcollection of a document, when converted to a map keyed by the ID/path of the role
 * documents. It stores the source of truth for:
 *  - What permissions a non-owner user has wrt the document
 *  - What permissions an anonymous user has wrt the document
 *  - What permissions an organization has wrt the document
 */
export interface DocumentPermissionsMap {
    [key: DocumentPermissionsKey]: DocumentPermissionsData;
}

/**
 * Base schema for new-style role entries with string permission
 */
const RoleBase = z.object({
    permission: DocumentPermission,
    created_at: z.number(),
    updated_at: z.number(),
    /** UID of the user who granted this permission (audit trail). Injected at the write boundary. */
    shared_by: z.string().optional(),
    /** Set when this role doc was collapsed into its canonical (prefixed) sibling; see DocIdSuperseded. */
    // .catch(undefined): a malformed marker coerces to absent so it never throws on read nor
    // silently retires a live role (fail open on access). See isDocIdSuperseded.
    _docid_superseded: DocIdSuperseded.optional().catch(undefined),
});

/**
 * New-style role entry for a specific user
 */
export const UserRole = RoleBase.extend({
    account_type: z.literal("user"),
    account_uid: PrefixedUserUid,
});
export type UserRole = z.infer<typeof UserRole>;

/**
 * New-style role entry for public/anonymous access.
 */
export const AnonymousRole = RoleBase.extend({
    account_type: z.literal("user"),
    account_uid: z.literal(ANON_ROLE_UID),
});
export type AnonymousRole = z.infer<typeof AnonymousRole>;

/**
 * New-style role entry for an organization
 */
export const OrgRole = RoleBase.extend({
    account_type: z.literal("organization"),
    account_uid: OrganizationUid,
});
export type OrgRole = z.infer<typeof OrgRole>;

const LegacyNewUserRole = RoleBase.extend({
    account_type: z.literal("user"),
    account_uid: z
        .string()
        .refine(
            (uid) =>
                uid !== ANON_ROLE_UID && !uid.startsWith("usr_") && !isOrganizationUid(uid) && !isEnterpriseUid(uid),
            "Expected a bare legacy user UID",
        ),
});

/**
 * Timestamps as legacy role entries actually carry them in Firestore: a
 * millisecond number, a numeric string (some 2020-era writes stored "1"), or
 * absent on the oldest entries. Absent normalizes to 0 (epoch, an honest
 * "unknown") rather than inventing a date.
 */
const LegacyTimestamp = z.union([z.number(), z.string().regex(/^\d+$/).transform(Number)]).default(0);

/**
 * Legacy role entry format - accepts existing Firestore documents as-is.
 * May also contain a string permission field (e.g. "withdrawn") written by newer code.
 *
 * The preprocess reads the pre-2020-rename principal field `uid` as
 * `user_uid`; entries written before the rename still exist in Firestore.
 */
export const LegacyRole = z.preprocess(
    (value) =>
        value !== null && typeof value === "object" && !("user_uid" in value) && "uid" in value
            ? {...value, user_uid: (value as Record<string, unknown>).uid}
            : value,
    z.object({
        user_uid: z
            .string()
            .refine((uid) => !isEnterpriseUid(uid), "Enterprise UIDs are not document permission principals"),
        permission_type: z.nativeEnum(PermissionType),
        permission: DocumentPermission.optional(),
        created_at: LegacyTimestamp,
        updated_at: LegacyTimestamp,
        _docid_superseded: DocIdSuperseded.optional().catch(undefined),
    }),
);
export type LegacyRole = z.infer<typeof LegacyRole>;

interface NormalizedRoleBase {
    permission: DocumentPermission;
    permission_type: PermissionType;
    created_at: number;
    updated_at: number;
    shared_by?: string | undefined;
    _docid_superseded?: DocIdSuperseded | undefined;
}

export interface NormalizedUserRole extends NormalizedRoleBase {
    account_type: "user";
    account_uid: PrefixedUserUid;
    user_uid: PrefixedUserUid;
}

export interface NormalizedAnonymousRole extends NormalizedRoleBase {
    account_type: "user";
    account_uid: typeof ANON_ROLE_UID;
    user_uid: typeof ANON_ROLE_UID;
}

export interface NormalizedOrgRole extends NormalizedRoleBase {
    account_type: "organization";
    account_uid: OrganizationUid;
    user_uid: OrganizationUid;
}

/**
 * The normalized output type for document permissions data.
 * Includes both new-style fields (account_type, account_uid, permission) and
 * legacy fields (user_uid, permission_type) for backward compatibility.
 *
 * New code should prefer account_type/account_uid/permission.
 * Legacy fields are provided so existing consuming code continues to work
 * during the migration period.
 */
export type NormalizedDocumentPermissionsData = NormalizedUserRole | NormalizedAnonymousRole | NormalizedOrgRole;

function invalidCorrelatedIdentity(role: unknown): boolean {
    if (typeof role !== "object" || role === null) return false;

    const candidate = role as Readonly<Record<string, unknown>>;
    if (candidate.account_type === "user" && typeof candidate.account_uid === "string") {
        return isOrganizationUid(candidate.account_uid) || isEnterpriseUid(candidate.account_uid);
    }
    if (candidate.account_type === "organization" && typeof candidate.account_uid === "string") {
        return !isOrganizationUid(candidate.account_uid);
    }
    return false;
}

/**
 * Reads from Firestore: accepts all three shapes (UserRole, OrgRole, LegacyRole),
 * normalizes to a consistent format with both new and legacy fields.
 *
 * The output always includes:
 * - New fields: account_type, account_uid, permission
 * - Legacy fields: user_uid, permission_type (for backward compatibility)
 */
export const DocumentPermissionsData = z
    .unknown()
    .refine((role) => !invalidCorrelatedIdentity(role), "Permission role account type does not match its UID")
    .pipe(z.union([UserRole, AnonymousRole, OrgRole, LegacyNewUserRole, LegacyRole]))
    .transform((role): NormalizedDocumentPermissionsData => {
        if ("account_type" in role && role.account_type === "organization") {
            return {
                ...role,
                user_uid: role.account_uid,
                permission_type: documentPermissionToPermissionType(role.permission),
            };
        }
        if ("account_type" in role && role.account_uid === ANON_ROLE_UID) {
            return {
                ...role,
                account_uid: ANON_ROLE_UID,
                user_uid: ANON_ROLE_UID,
                permission_type: documentPermissionToPermissionType(role.permission),
            };
        }
        if ("account_type" in role) {
            // Read-side tolerance: the union refines already reject non-user principals
            // for this branch, so prefixing here cannot launder an org/ent/anon UID.
            const [, accountUid] = userUidForms(role.account_uid);
            return {
                ...role,
                account_uid: accountUid,
                user_uid: accountUid,
                permission_type: documentPermissionToPermissionType(role.permission),
            };
        }
        // Legacy entry: add new-style fields (shared_by not available).
        // Preserve an existing string permission (e.g. "withdrawn") rather than
        // unconditionally deriving it from permission_type, since "withdrawn" has
        // no numeric equivalent and would be lost.
        // Also sync permission_type from the effective permission so downstream
        // consumers that read permission_type directly see a consistent value.
        const effectivePermission = role.permission ?? permissionTypeToDocumentPermission(role.permission_type);
        if (isOrganizationUid(role.user_uid)) {
            return {
                ...role,
                account_type: "organization",
                account_uid: role.user_uid,
                user_uid: role.user_uid,
                permission: effectivePermission,
                permission_type: documentPermissionToPermissionType(effectivePermission),
            };
        }
        if (role.user_uid === ANON_ROLE_UID) {
            return {
                ...role,
                account_type: "user",
                account_uid: ANON_ROLE_UID,
                user_uid: ANON_ROLE_UID,
                permission: effectivePermission,
                permission_type: documentPermissionToPermissionType(effectivePermission),
            };
        }
        const [, accountUid] = userUidForms(role.user_uid);
        return {
            ...role,
            account_type: "user",
            account_uid: accountUid,
            user_uid: accountUid,
            permission: effectivePermission,
            permission_type: documentPermissionToPermissionType(effectivePermission),
        };
    });
export type DocumentPermissionsData = z.output<typeof DocumentPermissionsData>;
